import { HgSupportedLanguage, type MenuItem } from '@smg-real-estate/domus';
import { useHead } from '@unhead/vue';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useSignOut } from '@/auth/useSignOut';
import { useImpersonation } from '@/composables/useImpersonation';
import { handleNavTracking } from '@/helpers/trackingHelper';
import { isIS24User } from '@/helpers/userUtils';
import { LoginStatus } from '@/services/tracking/GtmTrackingService';
import { useFeatureFlagsStore } from '@/stores/featureFlags';
import { useUserStore } from '@/stores/user';

export function useApp() {
  const envBaseUrl = import.meta.env.VUE_APP_BASE_URL!;

  const { locale, t } = useI18n();

  const featureFlagsStore = useFeatureFlagsStore();
  const userStore = useUserStore();
  const { isImpersonation: hideMainNavigation } = useImpersonation();

  const isLoggedIn = computed(() => userStore.loginStatus === LoginStatus.LOGGED_IN);

  watch(
    () => userStore.hasUser,
    async (hasUser) => {
      if (hasUser) {
        await featureFlagsStore.fetchFlagSets();
      }
    },
    { immediate: true },
  );

  const { signOut } = useSignOut();

  function handleSignOut(event: Event) {
    event.preventDefault();
    void signOut();
  }

  function handleMenuClick(menuItem: MenuItem, event: Event) {
    if ((menuItem as any).href?.includes('sign-out')) {
      handleSignOut(event);
    }
  }

  function handleMainNavClick(menuItem: MenuItem, event: Event) {
    handleMenuClick(menuItem, event);
    handleNavTracking(menuItem, event, false, locale.value);
  }

  function handleMobileMenuNavClick(menuItem: MenuItem, event: Event) {
    handleNavTracking(menuItem, event, true, locale.value);
  }

  const htmlLang = computed(() =>
    locale.value === HgSupportedLanguage.EN ? locale.value : `${locale.value}-CH`,
  );

  useHead({
    title: () => t('meta.title'),
    titleTemplate: `%s | ${isIS24User() ? 'immoscout24.ch' : 'homegate.ch'}`,
    meta: [
      {
        name: 'description',
        content: () => t('meta.description'),
      },
    ],
    htmlAttrs: {
      lang: htmlLang,
    },
  });

  return {
    envBaseUrl,
    handleSignOut,
    isLoggedIn,
    hideMainNavigation,
    handleMainNavClick,
    handleMobileMenuNavClick,
  };
}
